/**
 * Evaluate display-condition / ecommerce rules for the static cart drawer
 * upsell PRODUCT block only (not the whole drawer).
 *
 * Product and catalog rules are checked against cart line items (any line match).
 * Cart rules (total, count) use the live cart snapshot.
 */

function normalizeId(id) {
  if (id == null || id === "") return "";
  const raw = String(id);
  return raw.includes("/") ? raw.split("/").pop() : raw;
}

function parseTags(tags) {
  if (Array.isArray(tags)) return tags.map((t) => String(t).trim()).filter(Boolean);
  if (typeof tags === "string" && tags.trim()) {
    return tags.split(",").map((t) => t.trim()).filter(Boolean);
  }
  return [];
}

export function normalizeCartLineToProductContext(cartLine) {
  if (!cartLine || typeof cartLine !== "object") return null;

  const priceCents =
    cartLine.final_price ??
    cartLine.line_price ??
    cartLine.price ??
    cartLine.original_line_price ??
    0;
  const quantity = Number(cartLine.quantity) || 1;

  return {
    id: cartLine.product_id ?? cartLine.id,
    title: cartLine.product_title ?? cartLine.title ?? "",
    product_type: cartLine.product_type ?? cartLine.type ?? "",
    vendor: cartLine.vendor ?? "",
    tags: parseTags(cartLine.tags),
    available: cartLine.available !== false,
    price: Number(priceCents) / 100 / quantity,
    handle: cartLine.handle ?? "",
  };
}

export function normalizeCartLines(cart) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  return items.map(normalizeCartLineToProductContext).filter(Boolean);
}

export function buildCartInfoFromCart(cart) {
  const totalCents = Number(cart?.total_price ?? cart?.items_subtotal_price ?? 0);
  const itemCount = Number(cart?.item_count);
  return {
    total: Number.isFinite(totalCents) ? totalCents / 100 : 0,
    itemCount: Number.isFinite(itemCount)
      ? itemCount
      : (cart?.items || []).reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0),
  };
}

export function evaluateCondition(fieldValue, operator, conditionValue) {
  const op = operator || "equals";
  switch (op) {
    case "equals":
      return fieldValue === conditionValue;
    case "not_equals":
      return fieldValue !== conditionValue;
    case "contains":
      if (Array.isArray(fieldValue)) {
        const needle = String(conditionValue).toLowerCase();
        return fieldValue.some((entry) =>
          String(entry).toLowerCase().includes(needle)
        );
      }
      return String(fieldValue).toLowerCase().includes(String(conditionValue).toLowerCase());
    case "not_contains":
      if (Array.isArray(fieldValue)) {
        const needle = String(conditionValue).toLowerCase();
        return !fieldValue.some((entry) =>
          String(entry).toLowerCase().includes(needle)
        );
      }
      return !String(fieldValue).toLowerCase().includes(String(conditionValue).toLowerCase());
    case "starts_with":
      return String(fieldValue).toLowerCase().startsWith(String(conditionValue).toLowerCase());
    case "ends_with":
      return String(fieldValue).toLowerCase().endsWith(String(conditionValue).toLowerCase());
    case "greater_than":
      return Number(fieldValue) > Number(conditionValue);
    case "less_than":
      return Number(fieldValue) < Number(conditionValue);
    case "greater_or_equal":
      return Number(fieldValue) >= Number(conditionValue);
    case "less_or_equal":
      return Number(fieldValue) <= Number(conditionValue);
    default:
      return true;
  }
}

function ruleResult(passed, reason, action) {
  return { passed, reason, action };
}

function checkProductMatch(condition, productData) {
  if (!condition) return true;

  const offerTriggered = condition.offer_is_triggered;
  if (offerTriggered?.type) {
    const { type, value } = offerTriggered;
    switch (type) {
      case "all_product":
        return true;
      case "all_except":
        return !!(
          productData &&
          Array.isArray(value) &&
          !value.some((p) => normalizeId(p.id) === normalizeId(productData.id))
        );
      case "specific_product":
        return !!(
          productData &&
          Array.isArray(value) &&
          value.some((p) => normalizeId(p.id) === normalizeId(productData.id))
        );
      case "product_specific_title":
        return !!(
          productData?.title &&
          Array.isArray(value) &&
          value.some((val) => productData.title.includes(val))
        );
      case "product_specific_tag":
        return !!(
          productData &&
          Array.isArray(productData.tags) &&
          Array.isArray(value) &&
          productData.tags.some((tag) => value.includes(tag))
        );
      case "product_specific_vendor":
        return !!(
          productData?.vendor &&
          Array.isArray(value) &&
          value.includes(productData.vendor)
        );
      default:
        return true;
    }
  }

  return true;
}

function evaluateProductTriggerRuleForLine(field, operator, value, action, productData) {
  if (!productData) {
    if (field === "product_trigger_all") {
      const shouldShow =
        value === true || value === "true" || value === "True (All Products)";
      const passed = action === "show" ? shouldShow : !shouldShow;
      return ruleResult(
        passed,
        passed ? "All products trigger allowed" : "All products trigger blocked",
        action
      );
    }
    return ruleResult(false, "No cart product context", action);
  }

  let fieldValue;
  switch (field) {
    case "product_trigger_all":
      fieldValue = true;
      break;
    case "product_trigger_specific":
    case "product_trigger_except":
      fieldValue = normalizeId(productData.id);
      break;
    case "product_trigger_title":
      fieldValue = productData.title || "";
      break;
    case "product_trigger_tag":
      fieldValue = Array.isArray(productData.tags) ? productData.tags : [];
      break;
    case "product_trigger_vendor":
      fieldValue = productData.vendor || "";
      break;
    default:
      return ruleResult(true, "Unknown product trigger", action);
  }

  if (!operator) {
    switch (field) {
      case "product_trigger_all": {
        const shouldShow =
          value === true || value === "true" || value === "True (All Products)";
        return ruleResult(shouldShow, shouldShow ? "All products" : "Not all products", action);
      }
      case "product_trigger_specific": {
        const currentId = normalizeId(productData.id);
        const isInSpecific =
          Array.isArray(value) &&
          value.some((p) => normalizeId(p.id) === currentId);
        return ruleResult(isInSpecific, isInSpecific ? "In specific list" : "Not in list", action);
      }
      case "product_trigger_except": {
        const currentId = normalizeId(productData.id);
        const isInExcept =
          Array.isArray(value) &&
          value.some((p) => normalizeId(p.id) === currentId);
        return ruleResult(!isInExcept, !isInExcept ? "Not in except list" : "In except list", action);
      }
      case "product_trigger_title": {
        const titleMatch =
          Array.isArray(value) &&
          value.some((val) => productData.title && productData.title.includes(val));
        return ruleResult(titleMatch, titleMatch ? "Title match" : "Title mismatch", action);
      }
      case "product_trigger_tag": {
        const tagMatch =
          Array.isArray(value) &&
          Array.isArray(productData.tags) &&
          value.some((val) => productData.tags.includes(val));
        return ruleResult(tagMatch, tagMatch ? "Tag match" : "Tag mismatch", action);
      }
      case "product_trigger_vendor": {
        const vendorMatch = Array.isArray(value) && value.includes(productData.vendor);
        return ruleResult(vendorMatch, vendorMatch ? "Vendor match" : "Vendor mismatch", action);
      }
      default:
        return ruleResult(true, "Unknown trigger", action);
    }
  }

  if (field === "product_trigger_specific" && Array.isArray(value)) {
    const normalizedValue = value.map((p) => normalizeId(p.id));
    const passed = evaluateCondition(fieldValue, operator, normalizedValue);
    return ruleResult(passed, passed ? "Specific product match" : "Specific product mismatch", action);
  }

  if (field === "product_trigger_except" && Array.isArray(value)) {
    const normalizedValue = value.map((p) => normalizeId(p.id));
    const passed = !evaluateCondition(fieldValue, operator, normalizedValue);
    return ruleResult(passed, passed ? "Except list pass" : "Except list fail", action);
  }

  const passed = evaluateCondition(fieldValue, operator, value);
  return ruleResult(passed, passed ? `${field} passed` : `${field} failed`, action);
}

function evaluateProductTriggerRuleForCart(rule, cartLines) {
  const { field, operator, value, action } = rule;
  if (!cartLines.length) {
    if (field === "product_trigger_all") {
      const shouldShow =
        value === true || value === "true" || value === "True (All Products)";
      const passed = action === "show" ? shouldShow : !shouldShow;
      return ruleResult(passed, "Empty cart product trigger", action);
    }
    return ruleResult(false, "Cart is empty", action);
  }

  const anyPass = cartLines.some((line) =>
    evaluateProductTriggerRuleForLine(field, operator, value, action, line).passed
  );
  return ruleResult(anyPass, anyPass ? "Cart line matched trigger" : "No cart line matched", action);
}

function evaluateEcommerceDataRuleForCart(rule, cartLines, cartInfo) {
  const { field, operator, value, action } = rule;

  if (field === "cart_total" || field === "cart_product_count") {
    const fieldValue =
      field === "cart_total" ? cartInfo.total : cartInfo.itemCount;
    const passed = evaluateCondition(fieldValue, operator, value);
    return ruleResult(passed, passed ? `${field} passed` : `${field} failed`, action);
  }

  if (!cartLines.length) {
    return ruleResult(false, "Cart is empty", action);
  }

  const anyPass = cartLines.some((line) => {
    let fieldValue = "";
    switch (field) {
      case "product_name":
        fieldValue = line.title || "";
        break;
      case "product_price":
        fieldValue = line.price || 0;
        break;
      case "product_available":
        fieldValue = line.available || false;
        break;
      case "product_tags":
        fieldValue = line.tags || [];
        break;
      case "product_type":
        fieldValue = line.product_type || "";
        break;
      case "product_vendor":
        fieldValue = line.vendor || "";
        break;
      default:
        return true;
    }
    return evaluateCondition(fieldValue, operator, value);
  });

  return ruleResult(
    anyPass,
    anyPass ? `${field} matched cart line` : `${field} not matched`,
    action
  );
}

function checkLegacyProductConditionsForCart(product, cartLines) {
  const offerTriggered = product?.offer_is_triggered;
  if (!offerTriggered?.type) return true;
  if (offerTriggered.type === "all_product") return true;
  if (!cartLines.length) return false;
  return cartLines.some((line) =>
    checkProductMatch({ offer_is_triggered: offerTriggered }, line)
  );
}

function aggregateRuleResults(results) {
  const hideRules = results.filter((r) => r.action === "hide" && r.passed);
  if (hideRules.length > 0) return false;

  const showRules = results.filter((r) => r.action === "show");
  if (showRules.length > 0) {
    const passedShowRules = showRules.filter((r) => r.passed);
    if (passedShowRules.length === 0) return false;
  }

  return true;
}

/**
 * @param {object} popup
 * @param {{ cart?: object }} context
 * @returns {boolean}
 */
export function checkCartDrawerUpsellConditions(popup, context = {}) {
  const conditions = popup?.generalSettings?.condition || {};
  const ecommerce = conditions.ecommerce || {};
  const product = conditions.product || {};
  const cartLines = normalizeCartLines(context.cart);
  const cartInfo = buildCartInfoFromCart(context.cart || {});

  if (!ecommerce.enabled) {
    return true;
  }

  if (!Array.isArray(ecommerce.rules) || ecommerce.rules.length === 0) {
    return checkLegacyProductConditionsForCart(product, cartLines);
  }

  const results = ecommerce.rules.map((rule) => {
    if (String(rule?.field || "").startsWith("product_trigger_")) {
      return evaluateProductTriggerRuleForCart(rule, cartLines);
    }
    return evaluateEcommerceDataRuleForCart(rule, cartLines, cartInfo);
  });

  return aggregateRuleResults(results);
}

export function shouldShowCartDrawerUpsellBlock(popup, context = {}) {
  return checkCartDrawerUpsellConditions(popup, context);
}
